import type {ReactNode} from "react";
import {cn} from "@/lib/utils";

type SectionProps = {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Tighter padding for dense lists/tables */
  dense?: boolean;
};

/** Elevated card used for order-modal content blocks — template for large modal sections. */
export function OrderSection({title, description, icon, action, className, children, dense}: SectionProps) {
  return (
    <section
      className={cn(
        "admin-card overflow-hidden !p-0 admin-animate-fade",
        className
      )}
    >
      {title ? (
        <header className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <h4 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
              {icon ? <span className="admin-modal__icon !h-7 !w-7 [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</span> : null}
              <span className="truncate">{title}</span>
            </h4>
            {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </header>
      ) : null}
      <div className={cn(dense ? "p-4 sm:p-5" : "p-5 sm:p-6")}>{children}</div>
    </section>
  );
}

type FieldProps = {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  span?: 1 | 2;
};

export function OrderField({label, children, className, span = 1}: FieldProps) {
  return (
    <div className={cn(span === 2 && "sm:col-span-2", className)}>
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-foreground break-words">{children}</dd>
    </div>
  );
}

export function OrderFieldGrid({children, className}: {children: ReactNode; className?: string}) {
  return <dl className={cn("grid gap-x-6 gap-y-4 sm:grid-cols-2", className)}>{children}</dl>;
}

export function OrderStat({label, value}: {label: ReactNode; value: ReactNode}) {
  return (
    <div className="rounded-lg border border-border/50 bg-muted/40 px-3 py-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tracking-tight tabular-nums">{value}</p>
    </div>
  );
}

export function OrderDivider() {
  return <div className="my-4 h-px bg-border/70" />;
}
